import styled from '@emotion/styled';
import {
  Alert,
  Badge,
  Checkbox,
  Input,
  message,
  Modal,
  ModalProps,
  Pagination,
  Table,
  TableColumnsType,
  TableColumnType,
  Tag,
} from 'antd';
import CheckContainer from 'components/CheckContainer';
import StatusButton from 'components/StatusButton';
import { isStoreAliasFirst, useStoreNameConfig } from 'hooks/business/store/useStoreName';
import useMixingTokoText from 'hooks/business/useMixingTokoText';
import useCrud from 'hooks/useCrud';
import { uniqBy } from 'lodash-es';
import { useEffect, useRef, useState, useMemo } from 'react';
import Highlighter from 'react-highlight-words';
import { useTranslate } from '@tolgee/react';
import { MoveStoresBody, UnsetStore, unsetStoreList } from 'services/api/oms';
import { isNoRegionScenario, scenarioTypeI18nLabelMap } from 'views/Aide/Scenario/config';
import { useGetRegionLabel } from 'hooks/business/useRegion';

export const Container = styled.div`
  margin: -24px;
`;

export const Content = styled.div`
  display: flex;
  max-height: calc(100vh - 280px);
  padding: 16px;
`;

export const Selector = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border: solid 1px var(--theme-border-1);
  border-radius: 2px;
  overflow: hidden;
  > div {
    position: relative;
    padding: 0 12px;
    overflow: auto;
    > h3 {
      position: sticky;
      top: 0;
      height: 50px;
      padding: 16px 0;
      margin-bottom: 0;
      font-size: 16px;
      background: #fff;
      z-index: 1;
    }
  }
  > footer {
    flex: 0 0 48px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 8px;
    color: var(--theme-text-3);
    box-shadow: 0px -2px 8px 0px rgba(167, 178, 204, 0.1),
      inset 0px 1px 0px 0px rgba(240, 242, 245, 1);
    > div {
      display: flex;
      align-items: center;
    }
  }
`;

export const StoreSelector = styled(Selector)`
  flex: 1 0 0;
  > div {
    padding-bottom: 16px;
    > h3 {
      display: flex;
      justify-content: space-between;
      align-items: center;
      > .ant-input-search {
        width: 275px;
      }
    }
    thead {
      position: sticky;
      top: 50px;
      z-index: 1;
    }
  }
  > footer {
    padding: 0 28px;
  }
`;

export const TaskSelector = styled(Selector)`
  flex: 0 0 276px;
  margin-left: 10px;
  label {
    display: block;
    margin-bottom: 12px;
    .${({ theme }) => theme.getAntdPrefixedClassName('badge-status-text')} {
      color: var(--theme-text-3);
    }
  }
`;

export const Task = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2px;
  > span {
    flex: 0 0 auto;
    margin: 0 0 0 4px;
  }
  > div {
    flex: 1 0 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export interface MoveStoreModalProps extends ModalProps {
  scenario: Marketing.Scenario;
  platform: General.PlatformUpperCase;
  region?: General.UpperCaseRegion;
  onMove: (body: MoveStoresBody, reset: () => void) => Promise<any>;
}

function MoveStoreModal(props: MoveStoreModalProps) {
  const { visible, scenario, platform, region, onMove, ...otherProps } = props;

  const { scenario: scenarioType, tasks } = scenario;

  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();

  const { config: storeNameConfig } = useStoreNameConfig();

  const {
    orderReminderTitle: { text: orderReminderTitle },
  } = useMixingTokoText();

  const { loading, list, fetchList } = useCrud({
    readList: () =>
      unsetStoreList(scenarioType, {
        platform,
        region,
      }),
  });

  const [selectedStoreList, setSelectedStoreList] = useState<UnsetStore[]>([]);

  const [activeTask, setActiveTask] = useState<Util.ElementType<Marketing.Scenario['tasks']>>();

  const taskList = tasks.map((task) => {
    const { taskId, taskName, isDefault, enable } = task;

    return (
      <CheckContainer
        key={taskId}
        checked={taskId === activeTask?.taskId}
        onChange={(checked) => {
          if (checked) {
            setActiveTask(task);
          }
        }}
      >
        <Task>
          <div>{taskName}</div>
          {isDefault && <Tag>{t('marketing.默认')}</Tag>}
        </Task>
        <Badge
          status={enable ? 'success' : 'default'}
          text={enable ? t('marketing.启用中') : t('marketing.未启用')}
        />
      </CheckContainer>
    );
  });

  const taskSelector = (
    <TaskSelector>
      <div>
        <h3>{t('aide.选择任务')}</h3>
        {taskList}
      </div>
      <footer>{t('marketing.已选 x', { x: activeTask ? activeTask.taskName : '' })}</footer>
    </TaskSelector>
  );

  const [keywords, setKeywords] = useState('');

  const trimedKeywords = keywords.trim();

  const [filterRegionList, setFilterRegionList] = useState<General.UpperCaseRegion[]>([]);

  const filteredList = useMemo(() => {
    const regionFilteredList =
      filterRegionList.length === 0
        ? list || []
        : (list || []).filter(({ region }) => filterRegionList.includes(region));

    if (!trimedKeywords) {
      return regionFilteredList;
    }

    return regionFilteredList.filter(({ storeName, alias, externalStoreId }) => {
      return (
        storeName.toLowerCase().includes(trimedKeywords.toLowerCase()) ||
        alias.toLowerCase().includes(trimedKeywords.toLowerCase()) ||
        `${externalStoreId}`.includes(trimedKeywords)
      );
    });
  }, [list, trimedKeywords, filterRegionList]);

  const regionList = useMemo(() => {
    return (list || []).reduce<General.UpperCaseRegion[]>((res, { region }) => {
      if (res.includes(region)) {
        return res;
      }

      res.push(region);

      return res;
    }, []);
  }, [list]);

  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const displayList = useMemo(() => {
    return filteredList.slice((current - 1) * pageSize, current * pageSize);
  }, [filteredList, current, pageSize]);

  const storeChecked = (store: UnsetStore) =>
    selectedStoreList.some(({ storeId }) => storeId === store.storeId);

  const currentPageChecked = displayList.every(storeChecked);
  const currentPageIndeterminate = !currentPageChecked && displayList.some(storeChecked);

  const columns: TableColumnsType<UnsetStore> = [
    ...(displayList.length > 0
      ? [
          {
            key: 'checked',
            title: (
              <Checkbox
                checked={currentPageChecked}
                indeterminate={currentPageIndeterminate}
                onChange={({ target: { checked } }) => {
                  setSelectedStoreList((pre) => {
                    if (checked) {
                      return uniqBy([...pre, ...displayList], 'storeId');
                    }

                    return pre.filter(({ storeId }) =>
                      displayList.every((store) => store.storeId !== storeId),
                    );
                  });
                }}
              />
            ),
            width: 20,
            align: 'center',
            render: (_, store: UnsetStore) => {
              return (
                <Checkbox
                  checked={storeChecked(store)}
                  onChange={({ target: { checked } }) => {
                    setSelectedStoreList((pre) => {
                      if (checked) {
                        return uniqBy([...pre, store], 'storeId');
                      }

                      return pre.filter(({ storeId }) => storeId !== store.storeId);
                    });
                  }}
                />
              );
            },
          } as TableColumnType<UnsetStore>,
        ]
      : []),
    ...(isNoRegionScenario(scenarioType)
      ? [
          {
            key: 'region',
            dataIndex: 'region',
            title: t('aide.站点'),
            width: 150,
            filters: regionList.map((region) => {
              return {
                value: region,
                text: getRegionLabel(region),
              };
            }),
            render: (region: UnsetStore['region']) => getRegionLabel(region),
          } as const,
        ]
      : []),
    {
      key: 'storeName',
      title: t('marketing.店铺名称'),
      width: isNoRegionScenario(scenarioType) ? 250 : 400,
      render: (_, record: UnsetStore) => {
        const { region, storeName, alias } = record;

        const displayName = isStoreAliasFirst(storeNameConfig, platform, region)
          ? alias || storeName
          : storeName;

        return <Highlighter autoEscape searchWords={[keywords]} textToHighlight={displayName} />;
      },
    },
  ];

  const table = (
    <Table
      rowKey="storeId"
      loading={loading}
      columns={columns}
      dataSource={displayList}
      pagination={false}
      onChange={(_, { region }) => setFilterRegionList((region as any) || [])}
    />
  );

  const footer = list && list.length > 0 && (
    <footer>
      <div>
        <Checkbox
          checked={list.length === selectedStoreList.length}
          indeterminate={list.length > selectedStoreList.length && selectedStoreList.length > 0}
          onChange={(e) => setSelectedStoreList(e.target.checked ? list : [])}
        >
          {t('marketing.全选')}
        </Checkbox>
        <div>{t('marketing.已选 x', { x: selectedStoreList.length })}</div>
      </div>
      <Pagination
        size="small"
        current={current}
        pageSize={pageSize}
        total={filteredList.length}
        onChange={(current, pageSize) => {
          setCurrent(current);
          setPageSize(pageSize);
        }}
        showSizeChanger={false}
      />
    </footer>
  );

  const storeSelector = (
    <StoreSelector>
      <div>
        <h3>
          {`${t('marketing.店铺')}(${(list || []).length})`}
          <Input.Search
            allowClear
            placeholder={t('aide.请输入店铺名称/店铺别名/官方ID')}
            onSearch={(keywords) => {
              setKeywords(keywords);
              setCurrent(1);
            }}
          />
        </h3>
        {table}
      </div>
      {footer}
    </StoreSelector>
  );

  const alert = (
    <Alert
      banner
      type="warning"
      message={t('aide.店铺未加入任务，将不执行x', {
        x:
          scenarioType === 'ORDER_REMINDER'
            ? orderReminderTitle
            : t(scenarioTypeI18nLabelMap[scenarioType]),
      })}
    />
  );

  const content = (
    <Content>
      {storeSelector}
      {taskSelector}
    </Content>
  );

  const refsCurrent = {
    t,
    fetchList,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    if (!visible) {
      return;
    }

    refs.current.fetchList().catch(() => {
      message.error(refs.current.t('marketing.获取店铺列表失败，请稍后再试'));
    });
  }, [visible]);

  const reset = () => {
    setActiveTask(undefined);
    setSelectedStoreList([]);
  };

  const saveButton = (
    <StatusButton
      disabled={selectedStoreList.length === 0 || !activeTask}
      type="primary"
      onClick={() =>
        onMove(
          {
            stores: selectedStoreList,
            taskId: activeTask?.taskId || 0,
          },
          reset,
        )
      }
    >
      {t('aide.保存并生效')}
    </StatusButton>
  );

  return (
    <Modal
      title={t('aide.未加入任务的店铺')}
      visible={visible}
      width={800}
      maskClosable={false}
      footer={saveButton}
      {...otherProps}
    >
      <Container>
        {alert}
        {content}
      </Container>
    </Modal>
  );
}

export default MoveStoreModal;
